<template>
  <button
    ref="dropdownButton"
    type="button"
    class="relative flex rounded-full px-2 py-0-5 text-left text-xxs font-semibold whitespace-nowrap text-white uppercase"
    :class="[
      dark ? 'bg-headline' : 'bg-disabled-medium',
      disabled ? 'cursor-default' : 'cursor-pointer',
    ]"
    :disabled="disabled"
    @click.stop="toggleMenu"
  >
    {{ label }}
    <span
      v-if="!disabled"
      class="inset-y-0 ml-2 flex cursor-pointer place-self-center"
    >
      <icon-base
        height="6"
        width="10"
        icon="dropdown-arrow"
        icon-color="#FFFFFF"
      />
    </span>

    <div
      v-if="showMenu"
      ref="dropdownMenu"
      v-on-click-outside="onClickOutsideHandler"
      class="absolute left-0 z-30 mt-1 w-max"
    >
      <ul
        role="listbox"
        class="max-h-60 overflow-y-auto rounded-md bg-white pt-2-5 text-sm text-gray-500 shadow-lg ring-1 ring-black/5 focus:outline-hidden sm:text-sm"
      >
        <li
          v-for="(option, id) in workflowCategoriesToApplicationOptions"
          :key="id"
          class="flex grow-1 justify-start overflow-auto px-5 py-2-5 hover:bg-gray-200"
          role="option"
        >
          <lf-checkbox
            v-model="checkboxValue"
            name="workflow"
            :value="id"
            :disabled="model.status === DO_SAVE_TEMPLATE_MODES.publish"
            @change="handleCheckboxChange"
          >
            <span class="text-left font-normal">{{ option }}</span>
          </lf-checkbox>
        </li>
      </ul>
    </div>
  </button>
</template>

<script setup lang="ts">
import { type Ref, computed, ref } from "vue";
import { useStore } from "vuex";

import { deepEqual } from "fast-equals";

import { DO_SAVE_TEMPLATE_MODES } from "@/helpers/constants";
import { useTeleportV2 } from "@/hooks/elements";
import { useWorkflowCategoriesInfo } from "@/hooks/workflow";

import LfCheckbox from "@/components/ui/inputs/LfCheckbox.vue";

import type { IOrchestrationTemplate } from "@/models/orchestration";
import type { PropType } from "vue";

const MAX_SELECTED_DISPLAYED = 2;

const emit = defineEmits(["update:modelValue"]);

const props = defineProps({
  modelValue: {
    // added String to type because query-string library type is being parsed wrong
    type: [Array, String] as PropType<(number | string)[]>,
    required: true,
  },
  model: {
    type: Object as PropType<IOrchestrationTemplate>,
    required: true,
  },
  dark: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  value: {
    // ------------------------------
    // to remove warnings from console
    type: [String, Number, Object],
    // ------------------------------
  },
});

const { dispatch } = useStore();
const { workflowCategoriesToApplicationOptions } = useWorkflowCategoriesInfo();
const showMenu = ref(false);
const checkboxValue = ref((props.modelValue || []).map(String));
const initial = ref(props.modelValue);
const dropdownButton: Ref<HTMLInputElement | null> = ref(null);
const dropdownMenu = ref<HTMLElement | null>(null);

useTeleportV2({
  target: dropdownMenu,
  to: "#menus",
  offset: { top: 0 },
  checkForOverflow: true,
});

const label = computed(() => {
  const selectedCount = props.modelValue?.length || 0;
  if (!selectedCount) {
    return "Select workflow";
  }
  if (
    selectedCount ===
    Object.keys(workflowCategoriesToApplicationOptions.value).length
  ) {
    return "All Workflows";
  }
  if (selectedCount < MAX_SELECTED_DISPLAYED) {
    return props.modelValue
      .map(
        (id) =>
          workflowCategoriesToApplicationOptions.value[
            id as keyof typeof workflowCategoriesToApplicationOptions.value
          ]
      )
      .join(", ");
  }
  return `${selectedCount} workflows`;
});

const toggleMenu = () => {
  if (!showMenu.value) {
    document.body.click();
  }
  showMenu.value = !showMenu.value;
};

const closeMenu = () => {
  // We don't want to call this everytime it's clicked outside the element
  // only if the menu is open
  if (!showMenu.value) {
    return;
  }
  showMenu.value = false;
  // Make the update request only if values are changed
  if (deepEqual(initial.value.map(String), checkboxValue.value)) {
    return;
  }
  updateWorkflow(checkboxValue.value);
  initial.value = checkboxValue.value;
};

const handleCheckboxChange = () => {
  emit("update:modelValue", checkboxValue.value);
};

const updateWorkflow = (newWorkflow: string[]) => {
  dispatch("orchestration/updateTemplate", {
    data: {
      workflow: newWorkflow,
    },
    templateId: props.model?.id,
  });
};

const onClickOutsideHandler = [closeMenu, { capture: false }];
</script>
